import { Maximize2 } from 'lucide-react';
import { type ReactNode, type Ref, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

/** Same placeholder syntax the gateway fills (`{{name}}`). */
const VARIABLE = /(\{\{\s*[A-Za-z_][A-Za-z0-9_]*\s*\}\})/g;

/** One line of the prompt with its variables and Markdown headings marked up. */
function HighlightedLine({ text }: { text: string }) {
  if (text === '') return <>{'​'}</>;
  const heading = /^#{1,6}\s/.test(text);
  return (
    <span className={heading ? 'text-foreground font-semibold' : undefined}>
      {text.split(VARIABLE).map((part, i) =>
        i % 2 === 1 ? (
          <span key={i} className="bg-primary/10 text-primary rounded-sm">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </span>
  );
}

// Both layers must lay out text identically, or the caret drifts from the highlighting.
const TEXT_METRICS =
  'font-mono text-[13px] leading-6 whitespace-pre-wrap break-words [overflow-wrap:anywhere] [tab-size:2]';

function EditorSurface({
  id,
  value,
  onChange,
  disabled,
  placeholder,
  textareaRef,
  className,
  label,
}: {
  id: string;
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  placeholder?: string;
  textareaRef?: Ref<HTMLTextAreaElement>;
  className?: string;
  label: string;
}) {
  const lines = useMemo(() => value.split('\n'), [value]);
  return (
    <div
      className={cn(
        'bg-background focus-within:border-ring focus-within:ring-ring/50 overflow-auto rounded-md border focus-within:ring-3',
        disabled && 'bg-muted/40',
        className,
      )}
    >
      <div className="relative min-h-full py-3">
        <div aria-hidden className={cn('text-muted-foreground pointer-events-none', TEXT_METRICS)}>
          {lines.map((line, i) => (
            <div key={i} className="flex">
              <span className="text-muted-foreground/60 w-11 shrink-0 pr-3 text-right select-none">
                {i + 1}
              </span>
              <span className="text-foreground/90 min-w-0 flex-1 pr-3 pl-3">
                <HighlightedLine text={line} />
              </span>
            </div>
          ))}
        </div>
        {value === '' && placeholder && (
          <span
            aria-hidden
            className={cn(
              'text-muted-foreground pointer-events-none absolute top-3 right-3 left-11 pl-3',
              TEXT_METRICS,
            )}
          >
            {placeholder}
          </span>
        )}
        <textarea
          id={id}
          ref={textareaRef}
          aria-label={label}
          value={value}
          disabled={disabled}
          spellCheck={false}
          onChange={(e) => onChange(e.target.value)}
          className={cn(
            'caret-foreground selection:bg-primary/25 absolute inset-y-0 right-0 left-11 resize-none overflow-hidden bg-transparent px-3 py-3 text-transparent outline-none disabled:cursor-not-allowed',
            TEXT_METRICS,
          )}
        />
      </div>
    </div>
  );
}

function formatCount(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k` : String(n);
}

/**
 * A code-style editor for system prompts: line numbers, highlighted `{{variables}}` and Markdown
 * headings, size counts, and a full-screen mode. Plain text underneath (a textarea), so cursor
 * insertion, undo and accessibility work as usual.
 */
export function PromptEditor({
  id,
  label,
  value,
  onChange,
  disabled = false,
  placeholder,
  textareaRef,
  heading,
  toolbar,
  footer,
  className,
}: {
  id: string;
  label: string;
  /** Shown on the toolbar row, left of the controls (e.g. the field's visible label). */
  heading?: ReactNode;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  textareaRef?: Ref<HTMLTextAreaElement>;
  /** Extra controls shown before the full-screen button. */
  toolbar?: ReactNode;
  /** Shown under the counts (e.g. which variables the prompt uses). */
  footer?: ReactNode;
  /** Height of the inline editor. */
  className?: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const lineCount = value === '' ? 0 : value.split('\n').length;
  // A rough guide only: about four characters per token for English text.
  const tokens = Math.ceil(value.length / 4);

  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-end gap-2">
        {heading && <div className="mr-auto">{heading}</div>}
        {toolbar}
        <Button type="button" size="sm" variant="ghost" onClick={() => setExpanded(true)}>
          <Maximize2 aria-hidden />
          Full screen
        </Button>
      </div>
      {!expanded && (
        <EditorSurface
          id={id}
          label={label}
          value={value}
          onChange={onChange}
          disabled={disabled}
          placeholder={placeholder}
          textareaRef={textareaRef}
          className={className ?? 'h-[min(60vh,32rem)] min-h-64'}
        />
      )}
      <div className="text-muted-foreground flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <span>
          {lineCount} {lineCount === 1 ? 'line' : 'lines'}
        </span>
        <span>{formatCount(value.length)} characters</span>
        <span title="Estimated at four characters per token">
          About {formatCount(tokens)} tokens
        </span>
        {footer}
      </div>
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="flex h-[min(90dvh,60rem)] flex-col sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle>{label}</DialogTitle>
            <DialogDescription>
              Changes save the same way as in the page. Press Escape to go back.
            </DialogDescription>
          </DialogHeader>
          <EditorSurface
            id={`${id}-full`}
            label={label}
            value={value}
            onChange={onChange}
            disabled={disabled}
            placeholder={placeholder}
            className="min-h-0 flex-1"
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
